import { isExpired } from './signedUrlExpiry'

const DEFAULT_RESIGN_INTERVAL_MS = 10 * 60 * 1000
const RESIGN_BEFORE_EXPIRY_MS = 60 * 1000
const UNUSABLE_BEFORE_EXPIRY_MS = 30 * 1000
const RETRY_AFTER_FAILURE_MS = 60 * 1000
const RESIGN_DEDUPE_WINDOW_MS = 2 * 1000
const MAX_TIMER_MS = 2 ** 31 - 1

export type SignedUrlBatch = { expiresAt?: number }

/** A batch that is dead, or within half a minute of it, must not reach an `<img>` or a fetch. */
export function isSignedUrlBatchUnusable(
  batch: SignedUrlBatch | undefined,
  now = Date.now()
): boolean {
  return isExpired(batch?.expiresAt, now, UNUSABLE_BEFORE_EXPIRY_MS)
}

/**
 * Delay before a batch should be re-signed: a minute before it expires, at
 * once when it is already unusable, every ten minutes when its expiry is
 * unknown, and a minute after a failed re-sign (never sooner than the
 * pre-expiry lead). Nothing is scheduled before the first batch arrives.
 */
export function signedUrlResignDelay(
  batch: SignedUrlBatch | undefined,
  failed: boolean,
  now = Date.now()
): number | undefined {
  if (batch === undefined) return undefined
  if (batch.expiresAt === undefined) {
    return failed ? undefined : DEFAULT_RESIGN_INTERVAL_MS
  }
  const untilLead = batch.expiresAt - RESIGN_BEFORE_EXPIRY_MS - now
  if (failed) return Math.max(RETRY_AFTER_FAILURE_MS, untilLead)
  return Math.min(MAX_TIMER_MS, Math.max(0, untilLead))
}

const lastResignByBatch = new WeakMap<SignedUrlBatch, number>()

/**
 * Arms the re-sign timer for one mounted consumer and returns its cancel.
 * Consumers sharing a batch (the same SWR cache entry) collapse into one
 * Rolodex request, so a remount with ten page images costs one round trip.
 */
export function scheduleSignedUrlResign(
  batch: SignedUrlBatch | undefined,
  failed: boolean,
  resign: () => void
): () => void {
  const delay = signedUrlResignDelay(batch, failed)
  if (batch === undefined || delay === undefined) return () => {}
  const timer = setTimeout(() => {
    const now = Date.now()
    const last = lastResignByBatch.get(batch)
    if (last !== undefined && now - last < RESIGN_DEDUPE_WINDOW_MS) return
    lastResignByBatch.set(batch, now)
    resign()
  }, delay)
  return () => clearTimeout(timer)
}
